import { AxiosError } from 'axios';

/**
 * Interface para erros padronizados da aplicação
 */
export interface AppError {
  message: string;
  status?: number;
  code?: string;
  details?: string;
  friendlyMessage: string;
}

/**
 * Mensagens amigáveis para diferentes tipos de erro
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Erros de rede
  NETWORK_ERROR: 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.',
  TIMEOUT_ERROR: 'A requisição demorou muito para responder. Tente novamente.',
  CONNECTION_REFUSED: 'Servidor indisponível. Tente novamente em alguns instantes.',
  
  // Erros HTTP
  '400': 'Dados inválidos fornecidos. Verifique as informações e tente novamente.',
  '401': 'Sua sessão expirou. Faça login novamente.',
  '403': 'Você não tem permissão para realizar esta ação.',
  '404': 'O recurso solicitado não foi encontrado.',
  '409': 'Este registro já existe. Verifique os dados informados.',
  '429': 'Muitas requisições. Aguarde um momento antes de tentar novamente.',
  '500': 'Erro interno do servidor. Nossa equipe foi notificada e está trabalhando para resolver.',
  '502': 'Servidor temporariamente indisponível. Tente novamente em alguns instantes.',
  '503': 'Serviço temporariamente indisponível. Tente novamente em alguns instantes.',
  '504': 'Tempo de resposta excedido. Tente novamente.',
  
  // Erros específicos da aplicação
  CAMPAIGN_NOT_FOUND: 'Campanha não encontrada.',
  CAMPAIGN_PAUSED: 'A campanha está indisponível no momento. Tente novamente mais tarde.',
  CAMPAIGN_ENDED: 'A campanha já encerrou. Não é mais possível cadastrar cupons.',
  COUPON_ALREADY_EXISTS: 'Este número de cupom já foi cadastrado. Use um número diferente.',
  USER_NOT_FOUND: 'Usuário não encontrado.',
  INVALID_CREDENTIALS: 'Email ou senha incorretos.',
  TOKEN_EXPIRED: 'Sua sessão expirou. Faça login novamente.',
  STORE_NOT_FOUND: 'Loja não encontrada.',
  SPONSOR_NOT_FOUND: 'Patrocinador não encontrado.',
};

/**
 * Mensagem padrão para erros desconhecidos
 */
const DEFAULT_ERROR_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.';

/**
 * Obtém mensagem amigável baseada no código de erro
 */
export const getFriendlyErrorMessage = (error: Error | AxiosError | unknown): string => {
  // Se já é uma mensagem amigável, retorna direto
  if (error instanceof Error && ERROR_MESSAGES[error.message]) {
    return ERROR_MESSAGES[error.message];
  }

  // Se é um AxiosError, tenta extrair informações
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    
    // Verifica status HTTP
    if (axiosError.response?.status) {
      const status = axiosError.response.status.toString();
      if (ERROR_MESSAGES[status]) {
        return ERROR_MESSAGES[status];
      }
    }

    // Verifica mensagem do backend
    const backendMessage = (axiosError.response?.data as any)?.message;
    if (backendMessage && typeof backendMessage === 'string') {
      // Verifica se a mensagem do backend está mapeada
      for (const [key, message] of Object.entries(ERROR_MESSAGES)) {
        if (backendMessage.toLowerCase().includes(key.toLowerCase())) {
          return message;
        }
      }
      // Se não encontrou, retorna a mensagem do backend
      return backendMessage;
    }

    // Verifica se é erro de rede
    if (!axiosError.response) {
      if (axiosError.code === 'ECONNABORTED' || axiosError.message.includes('timeout')) {
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      }
      if (axiosError.code === 'ERR_NETWORK' || axiosError.message.includes('Network Error')) {
        return ERROR_MESSAGES.NETWORK_ERROR;
      }
      return ERROR_MESSAGES.CONNECTION_REFUSED;
    }
  }

  // Se é um Error comum
  if (error instanceof Error) {
    // Verifica se a mensagem está mapeada
    for (const [key, message] of Object.entries(ERROR_MESSAGES)) {
      if (error.message.toLowerCase().includes(key.toLowerCase())) {
        return message;
      }
    }
    
    // Retorna a mensagem do erro se for descritiva
    if (error.message && error.message.length > 0) {
      return error.message;
    }
  }

  // Retorna mensagem padrão
  return DEFAULT_ERROR_MESSAGE;
};

/**
 * Cria um objeto AppError padronizado
 */
export const createAppError = (error: Error | AxiosError | unknown): AppError => {
  const friendlyMessage = getFriendlyErrorMessage(error);
  
  let status: number | undefined;
  let code: string | undefined;
  let details: string | undefined;
  let message: string = DEFAULT_ERROR_MESSAGE;

  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    status = axiosError.response?.status;
    code = axiosError.code;
    details = (axiosError.response?.data as any)?.details || axiosError.message;
    message = axiosError.message;
  } else if (error instanceof Error) {
    message = error.message;
    code = error.name;
    details = error.stack;
  }

  return {
    message,
    status,
    code,
    details,
    friendlyMessage,
  };
};

/**
 * Verifica se o erro é um erro de rede (sem resposta do servidor)
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    return !axiosError.response;
  }
  return false;
};

/**
 * Verifica se o erro é um erro de autenticação (401)
 */
export const isAuthError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status === 401;
  }
  return false;
};

/**
 * Verifica se o erro é um erro de permissão (403)
 */
export const isPermissionError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status === 403;
  }
  return false;
};

/**
 * Verifica se o erro é um erro de servidor (5xx)
 */
export const isServerError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status ? axiosError.response.status >= 500 : false;
  }
  return false;
};

/**
 * Verifica se o erro é um erro de validação (400)
 */
export const isValidationError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status === 400;
  }
  return false;
};

